// next/app/provider
'use client';

import { setDistinctIdForPostHog, setReferrer } from '@ninjacart/nc-events-sdk-v2';
import { onMessageReceived } from '@ninjacart/nc-nk-firebase';
import {
  trackNotificationSentEvent,
  useAppPersistenceStore,
  useEventZStore,
  useProfileStore,
} from '@ninjacart/nc-nk-store';
import { SharedBootstrapProvider } from '@ninjacart/nc-nk-web-deps';
import { getMessaging, onMessage } from 'firebase/messaging';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { useSearchParams } from 'solito/navigation';

interface WebProviderProps {
  children: React.ReactNode;
  initialBootstrapData: any;
  initialIPLocation: any;
  refCode?: string | string[];
}

export const WebProvider = ({
  children,
  initialBootstrapData,
  initialIPLocation,
  refCode,
}: WebProviderProps) => {
  const [isReady, setIsReady] = useState(false);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const userProfile = useProfileStore.getState().userProfile;
  const referralCode = searchParams?.get('refCode');
  const appLanguage = useAppPersistenceStore.getState()?.appLanguage ?? 'en';
  const foregroundMessageUnsubscribeRef = useRef<(() => void) | null>(null);
  // const { showAd, loaded, isAdClosed, isError } = useInterstitialAds({});
  // const initializeAdsShow = useEventZStore((state) => state.initializeAdsShow);
  // const userFirstOpenTime = useEventZStore((state) => state.userFirstOpenTime);
  // const [isFirstOpen, setIsFirstOpen] = useState<boolean | null>(null);
  // const adAlreadyShown = useRef(false);
  // const setAdsEndVideoEnabled = useEventZStore((state) => state.setAdsEndVideoEnabled);

  // ✅ Detect first open using AsyncStorage
  // useEffect(() => {
  //   const checkFirstOpen = async () => {
  //     if (!userFirstOpenTime) {
  //       // First app open
  //       setIsFirstOpen(true);
  //       setAdsEndVideoEnabled(true);
  //     } else {
  //       // Not the first time
  //       setIsFirstOpen(false);
  //     }
  //   };
  //   checkFirstOpen();
  // }, []);

  // ✅ Show ad only if not first open, once per launch
  // useEffect(() => {
  //   if (isFirstOpen === false && initializeAdsShow && loaded && !adAlreadyShown.current) {
  //     adAlreadyShown.current = true;
  //     showAd();
  //   }
  // }, [loaded, initializeAdsShow, isFirstOpen, showAd]);

  // ✅ When ad closed OR failed OR ads disabled → allow access
  // useEffect(() => {
  //   if (isAdClosed || isError || !initializeAdsShow) {
  //     setAdsEndVideoEnabled(true);
  //   }
  // }, [isAdClosed, isError, initializeAdsShow]);

  useEffect(() => {
    if (referralCode) {
      sessionStorage.setItem('refCode', referralCode);
    }
  }, [referralCode]);

  useEffect(() => {
    const installReferrer = window?.parent?.location?.href;
    if (installReferrer) {
      setReferrer(installReferrer);
    }
  }, [pathname]);

  const initializeForegroundMessaging = useCallback(async () => {
    try {
      foregroundMessageUnsubscribeRef.current?.();
      const firebaseApp = (await import('@ninjacart/nc-nk-firebase')).getFirebaseApp;
      const messaging = getMessaging(firebaseApp());

      const unsubscribe = onMessage(messaging, async (payload) => {
        await onMessageReceived(payload, appLanguage);
        const {
          notification: { title: notificationTitle } = {},
          data: {
            campaignId,
            fcmMessageId,
            deeplinkingUrl,
            sourceReferenceId,
            sourceReferenceType,
            showTimer,
            startTime,
            endTime,
          } = {},
        } = payload;

        await trackNotificationSentEvent({
          notificationTitle: notificationTitle ?? '',
          campaignId,
          fcmMessageId,
          deeplinkingUrl,
          sourceReferenceId,
          sourceReferenceType,
          showTimer: showTimer ?? false,
          startTime,
          endTime,
        });
      });

      foregroundMessageUnsubscribeRef.current = unsubscribe;
    } catch (error) {
      console.error('Failed to initialize foreground messaging listener', error);
    }
  }, [appLanguage]);

  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return;
    }

    let permissionStatus: PermissionStatus | null = null;

    const maybeInitializeListener = () => {
      if (Notification.permission === 'granted') {
        void initializeForegroundMessaging();
      } else if (Notification.permission === 'denied') {
        foregroundMessageUnsubscribeRef.current?.();
        foregroundMessageUnsubscribeRef.current = null;
      }
    };

    maybeInitializeListener();

    if ('permissions' in navigator && navigator.permissions?.query) {
      navigator.permissions
        .query({ name: 'notifications' })
        .then((status) => {
          permissionStatus = status;
          status.onchange = () => {
            if (status.state === 'granted') {
              maybeInitializeListener();
            }

            if (status.state === 'denied') {
              foregroundMessageUnsubscribeRef.current?.();
              foregroundMessageUnsubscribeRef.current = null;
            }
          };
        })
        .catch((error) => {
          console.error('Failed to query notification permissions', error);
        });
    }

    return () => {
      foregroundMessageUnsubscribeRef.current?.();
      foregroundMessageUnsubscribeRef.current = null;
      if (permissionStatus) {
        permissionStatus.onchange = null;
      }
    };
  }, [initializeForegroundMessaging]);

  useEffect(() => {
    // console.log('App is ready for bootstrapping!!!');

    if (Platform.OS === 'web') {
      // console.log('Posthog user details => ', userProfile?.user?.id, userProfile?.user?.name);
      setDistinctIdForPostHog({
        userDetails: {
          userId: userProfile?.user?.id,
          name: userProfile?.user?.name,
        },
      });

      // if ('serviceWorker' in navigator) {
      //   navigator.serviceWorker.getRegistrations().then((registrations) => {
      //     console.log('Force updating service worker!!!');
      //     registrations.forEach((registration) => {
      //       console.log("Serwise status => ", registration);
      //       registration.update()
      //     });
      //   });
      // }

      // if (typeof window !== 'undefined' && !window.googletag) {
      //   const script = document.createElement('script');
      //   script.src = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';
      //   script.async = true;
      //   script.onload = () => {
      //     window.googletag = window.googletag || { cmd: [] };
      //     window.googletag.cmd.push(() => {
      //       window.googletag.pubads().enableSingleRequest();
      //       window.googletag.enableServices();
      //       window.googletag.pubads().enableLazyLoad({
      //         fetchMarginPercent: -1,
      //         // renderMarginPercent: 4,
      //         // mobileScaling: 2,
      //       });
      //     });
      //   };
      //   document.body.appendChild(script);
      // }
      // onReceiveMessage();
      // setIsReady(true);
    }
  }, []);

  return (
    <SharedBootstrapProvider
      initialBootstrapData={initialBootstrapData}
      initialIPLocation={initialIPLocation}
      refCode={refCode}
    >
      {children}
    </SharedBootstrapProvider>
  );
};
